/**
 * Auto Repair - Vehicles Page (Task 61)
 */

import { useEffect, useState } from 'react';
import { VehicleList } from '@/components/industry/shared/VehicleList';
import { VehicleForm } from '@/components/industry/shared/VehicleForm';
import { useIndustryVehicles } from '@/hooks/industry';
import { CompanyType } from '@/types/company';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { IndustryVehicle } from '@/types/industry';
import { MainLayout } from "@/components/MainLayout";

export default function AutoRepairVehiclesPage() {
  useEffect(() => {
    document.title = 'Veículos | Controle Financeiro';
  }, []);

  const { vehicles, isLoading, createVehicle, updateVehicle, deleteVehicle } = useIndustryVehicles({
    domain: CompanyType.AUTO_REPAIR,
  });
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingVehicle, setEditingVehicle] = useState<IndustryVehicle | undefined>();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { data: company } = useQuery({
    queryKey: ['current_company'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');
      const { data } = await supabase.from('companies').select('*').eq('user_id', user.id).single();
      return data;
    },
  });

  const { data: clients = [] } = useQuery({
    queryKey: ['clients', company?.id],
    queryFn: async () => {
      if (!company) return [];
      const { data } = await supabase
        .from('clients')
        .select('id, name')
        .eq('company_id', company.id)
        .eq('is_active', true)
        .order('name');
      return data || [];
    },
    enabled: !!company,
  });

  const handleSubmit = async (data: any) => {
    setIsSubmitting(true);
    try {
      const vehicleData = {
        client_id: data.client_id,
        metadata: {
          plate: data.plate,
          make: data.make,
          model: data.model,
          year: data.year,
          vin: data.vin,
          color: data.color,
          mileage: data.mileage,
          clientName: clients.find((c) => c.id === data.client_id)?.name,
        },
      };

      if (editingVehicle) {
        await updateVehicle({ id: editingVehicle.id, data: vehicleData });
        toast.success('Veículo atualizado');
      } else {
        await createVehicle(vehicleData);
        toast.success('Veículo cadastrado');
      }
      setIsFormOpen(false);
      setEditingVehicle(undefined);
    } catch (error) {
      toast.error('Erro ao salvar veículo');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
<MainLayout>
      <div className="container mx-auto py-6">
        <div className="flex items-center justify-center py-12">
          <div className="text-muted-foreground">Carregando...</div>
        </div>
      </div>
    
    </MainLayout>);
  }

  return (
<MainLayout>
    <div className="container mx-auto py-6 space-y-6">
      <div>
        <h1 className="text-3xl font-bold">Veículos</h1>
        <p className="text-muted-foreground">Cadastro de veículos dos clientes</p>
      </div>

      <VehicleList
        vehicles={vehicles || []}
        onEdit={(vehicle) => {
          setEditingVehicle(vehicle);
          setIsFormOpen(true);
        }}
        onDelete={async (vehicle) => {
          if (window.confirm('Excluir veículo?')) {
            await deleteVehicle(vehicle.id);
            toast.success('Veículo excluído');
          }
        }}
        onCreate={() => {
          setEditingVehicle(undefined);
          setIsFormOpen(true);
        }}
      />

      <Dialog open={isFormOpen} onOpenChange={setIsFormOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingVehicle ? 'Editar' : 'Novo'} Veículo</DialogTitle>
          </DialogHeader>
          <VehicleForm
            vehicle={editingVehicle}
            clients={clients}
            onSubmit={handleSubmit}
            onCancel={() => {
              setIsFormOpen(false);
              setEditingVehicle(undefined);
            }}
            isSubmitting={isSubmitting}
          />
        </DialogContent>
      </Dialog>
    </div>
  
    </MainLayout>);
}
